import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * PWAUpdateBanner
 * Listens for the 'sw-update-available' custom event fired by index.js
 * when a new service worker has installed and is waiting to activate.
 * Shows a dismissible banner prompting the user to reload.
 */
export function PWAUpdateBanner() {
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (event) => {
      setRegistration(event.detail.registration);
      setDismissed(false);
    };
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const handleUpdate = useCallback(() => {
    if (!registration?.waiting) return;
    // Tell the waiting SW to take over
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    // Reload once the new SW has taken control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }, [registration]);

  if (!registration || dismissed) return null;

  return (
    <div
      role="alert"
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50
                 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg
                 shadow-soft px-4 py-3 flex items-center gap-3"
    >
      <RefreshCw className="w-5 h-5 text-[#1E3A5F] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--theme-text-primary)]">
          Update available
        </p>
        <p className="text-xs text-[var(--theme-text-secondary)]">
          Reload to get the latest version.
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={handleUpdate}
          className="text-xs font-semibold text-[#1E3A5F] hover:underline"
        >
          Reload
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-xs text-[var(--theme-text-secondary)] hover:underline"
        >
          Later
        </button>
      </div>
    </div>
  );
}
